/**
 * Control de costos de Google Maps Routes API (SDK Movex).
 * Prioriza distancia Haversine local y limita llamadas a computeRoutes / computeRouteMatrix.
 */

import { GetDistance } from './GeoFunctions';
import {
    ENABLE_GOOGLE_ROUTES as ENABLE_GOOGLE_ROUTES_DEFAULT,
    MAX_ROUTE_CALLS_PER_SESSION,
    MAX_NEARBY_DRIVERS,
    CACHE_TTL_ROUTES_MS as ROUTE_CACHE_TTL_MS,
} from '../config/economyConfig';
import { debugLog } from './economyLogger';
import { carTypesMatch } from './carTypeUtils';

export const ALLOWED_ROUTE_REASONS = [
    'booking_confirmed',
    'booking_estimate',
    'admin_estimate',
    'driver_accepted',
];

export const BLOCKED_ROUTE_REASONS = [
    'matrix',
    'preview',
    'live_tracking',
    'map_movement',
    'map_move',
    'nearby_drivers',
    'nearby',
    'search_drivers',
    'auto_dispatch',
    'driver_list',
    'interval',
    undefined,
    null,
];

// --- Configuración global (sobreescribible desde settings.enable_google_routes) ---
export { MAX_ROUTE_CALLS_PER_SESSION, MAX_NEARBY_DRIVERS };
export const ROUTE_THROTTLE_MS = 8000;
export const COORD_DECIMALS = 4;

/** Velocidad media urbana (km/h) para ETA aproximado sin Google Routes. */
const AVG_SPEED_KMH = 40;

let sessionRouteCalls = 0;
const routeCache = new Map();
const lastCallAt = {};

/**
 * Distancia aproximada en kilómetros (Haversine).
 * Esta función usa distancia aproximada para evitar costos altos de Google Maps Routes API.
 */
export const calculateHaversineDistanceKm = (origin, destination) => {
    const lat1 = typeof origin === 'object' ? origin.lat ?? origin.latitude : parseFloat(String(origin).split(',')[0]);
    const lng1 = typeof origin === 'object' ? origin.lng ?? origin.longitude : parseFloat(String(origin).split(',')[1]);
    const lat2 = typeof destination === 'object' ? destination.lat ?? destination.latitude : parseFloat(String(destination).split(',')[0]);
    const lng2 = typeof destination === 'object' ? destination.lng ?? destination.longitude : parseFloat(String(destination).split(',')[1]);
    if (!Number.isFinite(lat1) || !Number.isFinite(lng1) || !Number.isFinite(lat2) || !Number.isFinite(lng2)) {
        return 0;
    }
    return GetDistance(lat1, lng1, lat2, lng2);
};

export const roundCoord = (value, decimals = COORD_DECIMALS) => {
    const n = parseFloat(value);
    if (!Number.isFinite(n)) return 0;
    const factor = Math.pow(10, decimals);
    return Math.round(n * factor) / factor;
};

export const roundCoordPair = (lat, lng) => `${roundCoord(lat)},${roundCoord(lng)}`;

const cacheKeyForRoute = (startLoc, destLoc, waypoints = '') => {
    const wp = waypoints ? `|wp:${waypoints}` : '';
    return `${roundCoordPair(...startLoc.split(',').map(Number))}->${roundCoordPair(...destLoc.split(',').map(Number))}${wp}`;
};

export const estimateTimeSecondsFromKm = (distanceKm) => {
    const km = Math.max(0, parseFloat(distanceKm) || 0);
    return Math.round((km / AVG_SPEED_KMH) * 3600);
};

/**
 * Estimación de ruta sin llamar a Google (distancia + tiempo aproximados).
 */
export const buildHaversineRouteEstimate = (startLoc, destLoc, waypoints = '') => {
    let totalKm = 0;
    const parts = [startLoc];
    if (waypoints) {
        waypoints.split('|').filter(Boolean).forEach((wp) => parts.push(wp));
    }
    parts.push(destLoc);
    for (let i = 0; i < parts.length - 1; i++) {
        totalKm += calculateHaversineDistanceKm(parts[i], parts[i + 1]);
    }
    const time_in_secs = estimateTimeSecondsFromKm(totalKm);
    return {
        distance_in_km: totalKm,
        time_in_secs,
        polylinePoints: '',
        approximate: true,
        source: 'haversine',
    };
};

export const formatApproxDistanceText = (distanceKm, convertToMile = false) => {
    const km = parseFloat(distanceKm) || 0;
    const value = convertToMile ? km / 1.609344 : km;
    const unit = convertToMile ? 'mi' : 'km';
    return `Aprox. ${value.toFixed(1)} ${unit}`;
};

export const formatApproxArrivalText = (distanceKm, convertToMile = false) =>
    formatApproxDistanceText(distanceKm, convertToMile);

/**
 * Ordena conductores por distancia Haversine y limita resultados.
 * No usa Google Routes API para listar conductores cercanos.
 */
export const sortNearbyDriversByDistance = (userLocation, drivers, options = {}) => {
    const {
        limit = MAX_NEARBY_DRIVERS,
        radiusKm = 10,
        convertToMile = false,
        carTypeName = null,
    } = options;

    if (!userLocation || !drivers || !drivers.length) return [];

    const pickupLat = userLocation.lat ?? userLocation.latitude;
    const pickupLng = userLocation.lng ?? userLocation.longitude;

    const withDistance = [];
    for (let i = 0; i < drivers.length; i++) {
        const driver = { ...drivers[i] };
        if (!driver?.location || driver.location.lat == null || driver.location.lng == null) continue;
        if (carTypeName && driver.carType && !carTypesMatch(driver.carType, carTypeName)) continue;

        let distanceKm = calculateHaversineDistanceKm(
            { lat: pickupLat, lng: pickupLng },
            { lat: driver.location.lat, lng: driver.location.lng }
        );
        let distance = distanceKm;
        if (convertToMile) distance = distanceKm / 1.609344;
        if (distance >= radiusKm) continue;

        driver.distance = distance;
        driver.distanceKm = distanceKm;
        withDistance.push(driver);
    }

    withDistance.sort((a, b) => (a.distance || 0) - (b.distance || 0));
    return withDistance.slice(0, limit);
};

export const buildDriverDistanceResults = (sortedDrivers, convertToMile = false) =>
    sortedDrivers.map((d) => ({
        found: true,
        distance_in_km: d.distanceKm ?? d.distance,
        time_in_secs: estimateTimeSecondsFromKm(d.distanceKm ?? d.distance),
        timein_text: formatApproxArrivalText(d.distance, convertToMile),
        approximate: true,
    }));

export const isGoogleRoutesEnabled = (settings) => {
    if (settings && typeof settings.enable_google_routes === 'boolean') {
        return settings.enable_google_routes;
    }
    return ENABLE_GOOGLE_ROUTES_DEFAULT;
};

/**
 * Solo se debe llamar Google Routes API cuando el servicio esté confirmado
 * o en tracking activo — nunca para listados ni preview del mapa.
 */
export const shouldCallGoogleRoutes = (context = {}) => {
    const { reason, settings, force = false } = context;

    if (force) return true;

    if (!isGoogleRoutesEnabled(settings)) {
        debugLog('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason: reason || 'disabled', detail: 'ENABLE_GOOGLE_ROUTES=false' });
        return false;
    }

    if (BLOCKED_ROUTE_REASONS.includes(reason)) {
        debugLog('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason });
        return false;
    }

    if (sessionRouteCalls >= MAX_ROUTE_CALLS_PER_SESSION) {
        debugLog('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason, detail: 'MAX_ROUTE_CALLS_PER_SESSION' });
        return false;
    }

    if (!ALLOWED_ROUTE_REASONS.includes(reason)) {
        debugLog('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason, detail: 'reason_not_allowed' });
        return false;
    }

    const throttleKey = `${reason}:${context.serviceId || ''}`;
    const now = Date.now();
    if (lastCallAt[throttleKey] && now - lastCallAt[throttleKey] < ROUTE_THROTTLE_MS) {
        debugLog('ROUTE_API_SKIPPED_USING_HAVERSINE', { reason, detail: 'throttled' });
        return false;
    }

    return true;
};

export const recordRouteApiCall = (reason, meta = {}) => {
    sessionRouteCalls += 1;
    lastCallAt[`${reason}:${meta.serviceId || ''}`] = Date.now();
    debugLog('ROUTE_API_CALL_EXECUTED', { reason, sessionRouteCalls, ...meta });
};

export const resetSessionRouteCalls = () => {
    sessionRouteCalls = 0;
};

export const getCachedRoute = (startLoc, destLoc, waypoints = '') => {
    const key = cacheKeyForRoute(startLoc, destLoc, waypoints);
    const entry = routeCache.get(key);
    if (!entry) return null;
    if (Date.now() - entry.savedAt > ROUTE_CACHE_TTL_MS) {
        routeCache.delete(key);
        return null;
    }
    return entry.data;
};

export const saveCachedRoute = (startLoc, destLoc, waypoints, route) => {
    const key = cacheKeyForRoute(startLoc, destLoc, waypoints);
    routeCache.set(key, { data: route, savedAt: Date.now() });
};

/** Polyline simple de 2 puntos para preview sin Google Routes. */
export const buildStraightLineCoords = (pickup, drop) => {
    if (!pickup?.lat || !drop?.lat) return [];
    return [
        { latitude: pickup.lat, longitude: pickup.lng },
        { latitude: drop.lat, longitude: drop.lng },
    ];
};
